<script>
import Layout from "@/router/layouts/main";
import EmptyState from "@/components/widgets/empty-state";
import { api } from "@/state/services";
import { MESES, MEDALHAS, fmtPeriodo, fmtAtualizadoEm, iniciais } from "@/utils/ranking";
import {
  RANKINGS_NACIONAIS,
  RANKINGS_NACIONAIS_POR_ID,
  GRUPOS_NACIONAL,
  INDICADORES_NACIONAL,
  ORDEM_DESEMPATE,
  fmtIndicador,
  fmtFalta,
  podeVerRanking,
} from "@/utils/ranking-nacional";
import {
  RANKINGS_ESTADUAIS_POR_ID,
  GRUPOS_ESTADUAL,
  RANKING_ESTADUAL_ATIVO,
  contextoEstadual,
  carregarContextoEstadual,
  sincronizarContextoEstadual,
} from "@/utils/ranking-estadual";
import { linkRegulamento } from "@/utils/ranking-regulamento";

// Atalho do aviso "sem diretoria" para o ranking equivalente do país.
const NACIONAL_PADRAO = "/biteams/rk-nacional-brasil-regional-adesao";

// Enquanto o primeiro cálculo do período roda no servidor, a API responde 202 "processing".
// O interceptor global (state/services/api.service.js) NÃO repete chamadas — e não deve ganhar
// esse comportamento só por causa desta tela —, então o polling mora aqui (igual a ranking.vue).
const POLL_INTERVALO_MS = 4000;
const POLL_MAX_TENTATIVAS = 45; // ~3 minutos

// Os meses anteriores a 09/2026 voltaram a aparecer nos filtros, mas com o aviso de que não servem
// para comparar com os fechamentos mensais (a base mudou: migrações, regionais novas etc.).
const INICIO_COMPARAVEL = { ano: 2026, mes: 9 };

/**
 * Tela única dos 10 rankings do menu "Ranking Nacional" (Brasil / Truck), portados de
 * Rankings > Ranking Nacional do biTeams. O ranking vem de `meta.rankingNacional` na rota.
 *
 * Também atende os 8 rankings do menu "Ranking Estadual" (`meta.rankingEstadual`): mesma tela e
 * mesmas regras, com a lista recortada pela diretoria do usuário (a API resolve a diretoria e
 * devolve `diretoria` na resposta). O Estadual não tem premiação (`ranking.semPremiacao`): sem
 * etiqueta/selo/texto/destaque de prêmio — o Nacional segue igual. Sem diretoria a API responde {status:'sem_diretoria'} e a tela
 * mostra um aviso no lugar do ranking (não é erro nem 403, para o interceptor não deslogar).
 *
 * Diferente do biTeams: a API já devolve o recorte pronto (TOP N + a linha de quem está logado),
 * nunca a lista inteira; não há exportação nem os botões de administração (BL, Nome, Desempate);
 * e a tela é pensada primeiro para o celular (cards abaixo de md, tabela só no desktop).
 */
export default {
  components: { Layout, EmptyState },
  data() {
    return {
      MEDALHAS,
      ano: null,
      mes: null,
      limites: null,
      dados: null,
      carregando: false,
      processando: false,
      erro: "",
      // Ranking Estadual acessado por quem não pertence a nenhuma diretoria (texto vem da API).
      semDiretoria: "",
    };
  },
  computed: {
    estadual() {
      return !!this.$route.meta.rankingEstadual;
    },
    // Id do ranking da rota — o watch abaixo recarrega quando ele muda (troca pelo menu).
    rankingId() {
      return this.$route.meta.rankingEstadual || this.$route.meta.rankingNacional;
    },
    ranking() {
      if (this.estadual) {
        return RANKINGS_ESTADUAIS_POR_ID[this.$route.meta.rankingEstadual];
      }
      return RANKINGS_NACIONAIS_POR_ID[this.$route.meta.rankingNacional] || RANKINGS_NACIONAIS[0];
    },
    // Ranking de Regional aberto por um consultor (acesso direto pela URL): a tela não carrega.
    bloqueado() {
      return !podeVerRanking(this.$store.state.account.user, this.ranking);
    },
    // Atalho para um ranking do Nacional que o usuário pode ver: o padrão (Regional Adesão) ou,
    // para consultor/secretária sem essa permissão, o primeiro liberado.
    atalhoNacional() {
      const user = this.$store.state.account.user;
      const padrao = RANKINGS_NACIONAIS.find((r) => r.path === NACIONAL_PADRAO);
      const alvo = podeVerRanking(user, padrao) ? padrao : RANKINGS_NACIONAIS.find((r) => podeVerRanking(user, r));
      return alvo ? alvo.path : "";
    },
    // Botão do final da tela: abre o regulamento já na seção deste ranking.
    regulamento() {
      return linkRegulamento(this.rankingId);
    },
    grupoLabel() {
      const grupos = this.estadual ? GRUPOS_ESTADUAL : GRUPOS_NACIONAL;
      const grupo = grupos.find((g) => g.key === this.ranking.grupo);
      return grupo ? grupo.label : "";
    },
    // Nome da diretoria: o da resposta (fonte da verdade) ou, antes dela, o do contexto da sessão.
    diretoriaLabel() {
      if (!this.estadual) return "";
      if (this.dados && this.dados.diretoria && this.dados.diretoria.label) return this.dados.diretoria.label;
      return contextoEstadual.diretoria ? contextoEstadual.diretoria.label : "";
    },
    empresaLabel() {
      return this.ranking.grupo === "truck" ? "APVS Truck" : "APVS Brasil";
    },
    // Ranking Estadual: sem premiação. Nos rankings do Nacional a flag não existe → false.
    semPremiacao() {
      return !!this.ranking.semPremiacao;
    },
    mostraRegional() {
      return this.ranking.entidade === "consultor";
    },
    menorVence() {
      return this.ranking.metrica === "inadimplencia";
    },
    // Critérios de desempate que este ranking apura, na ordem do biTeams. Consultor individual
    // não tem "Consultores Ativos" (só faz sentido para a equipe de uma regional).
    criteriosDesempate() {
      return ORDEM_DESEMPATE.filter(
        (k) => k !== this.ranking.metrica && !(this.ranking.entidade === "consultor" && k === "consultoresAtivos")
      ).map((k) => (k === "inadimplencia" ? "menor inadimplência" : INDICADORES_NACIONAL[k].label.toLowerCase()));
    },
    textoDesempate() {
      const lista = this.criteriosDesempate;
      if (!lista.length) return "";
      const juntos = lista.length > 1 ? lista.slice(0, -1).join(", ") + " e " + lista[lista.length - 1] : lista[0];
      return `Empates são decididos, nesta ordem, por: ${juntos}.`;
    },
    top() {
      return (this.dados && this.dados.top) || [];
    },
    podio() {
      return this.top.slice(0, 3);
    },
    eu() {
      return this.dados ? this.dados.eu : null;
    },
    euRanqueado() {
      return !!(this.eu && this.eu.participa && !this.eu.sem_resultado);
    },
    // Indicadores da própria pessoa: a métrica primeiro, depois os critérios de desempate.
    meusIndicadores() {
      if (!this.eu || !this.eu.indicadores) return [];
      const ordem = [this.ranking.metrica, ...ORDEM_DESEMPATE.filter((k) => k !== this.ranking.metrica)];
      return ordem
        .filter((k) => this.eu.indicadores[k] !== undefined && INDICADORES_NACIONAL[k])
        .map((k) => ({
          key: k,
          ...INDICADORES_NACIONAL[k],
          valor: this.eu.indicadores[k],
          principal: k === this.ranking.metrica,
        }));
    },
    periodoLabel() {
      return this.dados ? fmtPeriodo(this.dados.periodo) : "";
    },
    anos() {
      if (!this.limites) return [];
      const lista = [];
      for (let a = this.limites.ano_atual; a >= this.limites.ano_minimo; a--) lista.push(a);
      return lista;
    },
    meses() {
      if (!this.limites) return [];
      const max = this.ano === this.limites.ano_atual ? this.limites.mes_atual : 12;
      return MESES.slice(0, max).map((text, i) => ({ value: i + 1, text }));
    },
    // Período escolhido é anterior a 09/2026: mostra o aviso de dados não comparáveis.
    periodoNaoComparavel() {
      if (!this.ano || !this.mes) return false;
      const ano = Number(this.ano);
      return ano < INICIO_COMPARAVEL.ano || (ano === INICIO_COMPARAVEL.ano && Number(this.mes) < INICIO_COMPARAVEL.mes);
    },
    textoSemResultado() {
      if (this.estadual && this.ranking.metrica === "ticketMedio" && this.ranking.entidade === "regional") {
        return "Sua regional ainda não tem ticket médio neste período. Lembre que este ranking é só para regionais com base ativa do mês anterior acima de 100.";
      }
      return this.menorVence
        ? "Sua regional não tem carteira (ativos + inadimplentes) apurada neste período, então não entra na lista. Assim que houver, sua posição aparece aqui."
        : `${this.ranking.entidade === "regional" ? "Sua regional ainda não tem" : "Você ainda não tem"} ${this.ranking.metricLabel.toLowerCase()} neste período. Assim que tiver, a posição aparece aqui.`;
    },
    // Frase de "quanto falta" do bloco Sua posição, na unidade da métrica. Empate na métrica
    // aparece como empate decidido pelo desempate, e não como "faltam 0".
    mensagemProgresso() {
      const eu = this.eu;
      if (!this.euRanqueado) return "";
      if (eu.posicao === 1) return "Você lidera o ranking. Continue assim!";

      // No Ranking Nacional todo o TOP N é premiado: estar no TOP já é estar na premiação.
      // (O Estadual não tem premiação, mas a frase de progresso é a mesma — fala só de posição.)
      const topN = this.dados.top_n;
      const acima = eu.posicao - 1;
      if (eu.no_top) {
        return this.textoFalta(eu.falta_para_subir, `para alcançar o ${acima}º colocado`, `com o ${acima}º colocado`);
      }
      if (eu.falta_para_top !== null && eu.falta_para_top !== undefined) {
        return this.textoFalta(eu.falta_para_top, `para entrar no TOP ${topN}`, `com o ${topN}º colocado`);
      }
      return "";
    },
  },
  watch: {
    // Trocar de ranking pelo menu reaproveita o componente: volta ao período corrente.
    rankingId() {
      this.dados = null;
      this.semDiretoria = "";
      this.limites = null;
      this.ano = null;
      this.mes = null;
      this.carregar();
    },
  },
  created() {
    this.seq = 0;
    this.timer = null;
    this.tentativas = 0;
    // Ranking Estadual desligado (utils/ranking-estadual.js): URL direta volta para o início.
    if (this.estadual && !RANKING_ESTADUAL_ATIVO) {
      this.$router.replace("/");
      return;
    }
    // Acesso direto pela URL: garante o nome da diretoria no título antes da 1ª resposta.
    if (this.estadual) carregarContextoEstadual();
    this.carregar();
  },
  beforeDestroy() {
    this.cancelarPolling();
  },
  methods: {
    fmtAtualizadoEm,
    fmtIndicador,
    iniciais,

    fmtValor(v) {
      return fmtIndicador(v, this.ranking.fmt);
    },
    textoFalta(valor, acao, empate) {
      const n = Number(valor) || 0;
      if (n <= 0) {
        return `Você está empatado em ${this.ranking.metricLabel.toLowerCase()} ${empate}; a posição foi decidida pelos critérios de desempate.`;
      }
      return `${fmtFalta(n, this.ranking.metrica)} ${acao}.`;
    },
    classePodio(posicao) {
      return posicao <= 3 ? `is-pos-${posicao}` : "";
    },
    cancelarPolling() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },
    montarQuery() {
      const params = new URLSearchParams();
      if (this.ano && this.mes) {
        params.set("ano", this.ano);
        params.set("mes", this.mes);
      }
      return params.toString();
    },
    // `polling` = nova tentativa automática de um cálculo em andamento (mantém o contador).
    async carregar(polling = false) {
      this.cancelarPolling();
      if (!polling) this.tentativas = 0;

      // Troca de filtro/ranking no meio de uma requisição: só a resposta mais recente vale.
      const seq = ++this.seq;
      this.carregando = true;
      this.erro = "";
      this.semDiretoria = "";
      if (!polling) this.processando = false;

      if (this.bloqueado) {
        this.dados = null;
        this.carregando = false;
        return;
      }

      try {
        const query = this.montarQuery();
        const res = await api.get(query ? `${this.ranking.endpoint}?${query}` : this.ranking.endpoint);
        if (seq !== this.seq) return;

        const corpo = res.data || {};
        if (res.status === 202 || corpo.status === "processing") {
          if (this.tentativas >= POLL_MAX_TENTATIVAS) {
            this.processando = false;
            this.carregando = false;
            this.erro = "O cálculo está demorando mais que o normal. Tente novamente em alguns minutos.";
            return;
          }
          this.tentativas++;
          this.processando = true;
          this.timer = setTimeout(() => this.carregar(true), POLL_INTERVALO_MS);
          return;
        }

        if (corpo.status === "success") {
          this.aplicar(corpo);
          if (this.estadual && corpo.diretoria) sincronizarContextoEstadual(corpo.diretoria);
        } else if (corpo.status === "sem_diretoria") {
          sincronizarContextoEstadual(null); // perdeu o vínculo: o menu some sem esperar novo login
          this.dados = null;
          this.semDiretoria = corpo.message || "Seu cadastro não está vinculado a nenhuma diretoria do Ranking Estadual.";
        } else {
          this.erro = corpo.message || "Não foi possível carregar o ranking agora.";
        }
        this.processando = false;
        this.carregando = false;
      } catch (e) {
        if (seq !== this.seq) return;
        const msg = e && e.response && e.response.data && e.response.data.message;
        this.erro = msg || "Não foi possível carregar o ranking. Verifique sua conexão e tente novamente.";
        this.processando = false;
        this.carregando = false;
      }
    },
    aplicar(corpo) {
      this.dados = corpo;
      this.limites = corpo.limites;
      this.ano = corpo.periodo.ano;
      this.mes = corpo.periodo.mes;
    },
    onMes() {
      this.carregar();
    },
    onAno(valor) {
      this.ano = valor;
      // Voltar para o ano corrente com um mês que ainda não começou: ajusta para o último válido.
      if (this.limites && valor === this.limites.ano_atual && this.mes > this.limites.mes_atual) {
        this.mes = this.limites.mes_atual;
      }
      this.carregar();
    },
  },
};
</script>

<template>
  <Layout>
    <div class="rk-page">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link tag="a" to="/" aria-label="Início">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 22h14a2 2 0 0 0 2-2v-9a1 1 0 0 0-.29-.71l-8-8a1 1 0 0 0-1.41 0l-8 8A1 1 0 0 0 3 11v9a2 2 0 0 0 2 2zm5-2v-5h4v5zm-5-8.59 7-7 7 7V20h-3v-5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v5H5z"></path>
            </svg>
          </router-link>
        </li>
        <li class="breadcrumb-item">Rankings</li>
        <li class="breadcrumb-item">{{ estadual ? "Estadual" : "Nacional" }} {{ grupoLabel }}</li>
        <li class="breadcrumb-item d-none d-sm-block">{{ ranking.menuLabel }}</li>
      </ol>

      <div class="page-title-box mb-3">
        <h3 class="mb-1">{{ ranking.titulo }}</h3>
        <p v-if="diretoriaLabel" class="rk-diretoria mb-1">
          <i class="bx bx-map-pin" aria-hidden="true"></i>
          <span class="sr-only">Diretoria: </span>{{ diretoriaLabel }}
        </p>
        <p class="rk-subtitulo mb-2">{{ ranking.subtitulo }}</p>
        <div class="rk-tags">
          <span class="rk-tag">Mensal</span>
          <span class="rk-tag">{{ empresaLabel }}</span>
          <span v-if="estadual" class="rk-tag">Estadual</span>
          <span v-if="dados && !semPremiacao" class="rk-tag rk-tag-premio">
            <i class="bx bx-trophy" aria-hidden="true"></i> TOP {{ dados.top_n }} premiado
          </span>
        </div>
      </div>

      <!-- Filtros: só aparecem depois da 1ª resposta, que traz o período corrente e os limites
           (calculados no fuso de Brasília pelo servidor — o relógio do aparelho não decide). -->
      <div v-if="limites && !semDiretoria" class="card rk-filtros">
        <div class="card-body">
          <div class="rk-filtros-grid rk-filtros-grid-nacional">
            <div class="rk-campo">
              <label for="rkn-mes" class="rk-label">Mês</label>
              <b-form-select id="rkn-mes" v-model="mes" :options="meses" class="rk-select" @change="onMes" />
            </div>
            <div class="rk-campo">
              <label for="rkn-ano" class="rk-label">Ano</label>
              <b-form-select id="rkn-ano" :value="ano" :options="anos" class="rk-select" @change="onAno" />
            </div>
          </div>
        </div>
      </div>

      <!-- Período anterior a 09/2026: aviso fixo e bem visível (não é fechamento oficial). -->
      <div v-if="periodoNaoComparavel && !semDiretoria" class="alert alert-warning rk-aviso-periodo" role="alert">
        <i class="bx bx-error rk-aviso-periodo-icone" aria-hidden="true"></i>
        <div>
          <strong>Atenção:</strong> Os valores apresentados não devem ser utilizados para comparação com os resultados
          dos fechamentos mensais, pois podem ter ocorrido alterações na base de dados, como migrações, criação de
          regionais e outros ajustes operacionais.
          <strong class="d-block mt-2">
            Questionamentos referentes aos dados deste relatório para períodos anteriores a setembro de 2026 não serão
            considerados.
          </strong>
        </div>
      </div>

      <!-- Carregando / calculando -->
      <div v-if="carregando" class="card">
        <div class="card-body rk-carregando" role="status" aria-live="polite">
          <b-spinner variant="primary" />
          <div class="rk-carregando-titulo">
            {{ processando ? "Calculando o ranking deste período…" : "Carregando ranking…" }}
          </div>
          <div v-if="processando" class="rk-carregando-sub">
            Na primeira consulta do período isso pode levar alguns segundos. A tela atualiza sozinha.
          </div>
        </div>
      </div>

      <!-- Ranking de Regional aberto por consultor (acesso direto pela URL) -->
      <div v-else-if="bloqueado" class="card">
        <EmptyState
          icon="bx bx-lock-alt"
          title="Ranking exclusivo para regionais"
          text="Este ranking é só para regionais. As regras dele continuam disponíveis no regulamento abaixo."
        >
          <template v-if="atalhoNacional" #action>
            <b-button variant="default" :to="atalhoNacional">Ver os rankings de consultor</b-button>
          </template>
        </EmptyState>
      </div>

      <!-- Ranking Estadual sem diretoria (acesso direto pela URL) -->
      <div v-else-if="semDiretoria" class="card">
        <EmptyState icon="bx bx-map-alt" title="Ranking Estadual indisponível para o seu cadastro" :text="semDiretoria">
          <template v-if="atalhoNacional" #action>
            <b-button variant="default" :to="atalhoNacional">Ver o Ranking Nacional</b-button>
          </template>
        </EmptyState>
      </div>

      <!-- Erro -->
      <div v-else-if="erro" class="card" role="alert">
        <EmptyState icon="bx bx-error-circle" title="Não foi possível carregar o ranking" :text="erro">
          <template #action>
            <b-button variant="default" @click="carregar()">Tentar novamente</b-button>
          </template>
        </EmptyState>
      </div>

      <template v-else-if="dados">
        <!-- Estado do período -->
        <div class="rk-status" :class="dados.fechado ? 'is-fechado' : 'is-vivo'">
          <i :class="dados.fechado ? 'bx bx-lock-alt' : 'bx bx-time-five'" aria-hidden="true"></i>
          <span v-if="dados.fechado">
            <strong>Resultado oficial</strong> de {{ periodoLabel }}. As posições deste período não mudam mais.
          </span>
          <span v-else>
            <!-- `dados_em` = quando o último relatório do i4pro usado aqui foi baixado. `atualizado_em`
                 (hora do recálculo do ranking) não aparece mais: parecia data de atualização dos dados. -->
            <strong>Parcial ao vivo</strong> de {{ periodoLabel }}<template v-if="dados.dados_em">,
            com dados do i4pro de {{ fmtAtualizadoEm(dados.dados_em) }}</template>. As posições podem mudar até o fechamento.
          </span>
        </div>

        <!-- Sua posição -->
        <section class="card rk-eu" aria-labelledby="rkn-eu-titulo">
          <div class="card-body">
            <h2 id="rkn-eu-titulo" class="rk-secao-titulo">Sua posição</h2>

            <template v-if="euRanqueado">
              <div class="rk-eu-topo">
                <div class="rk-eu-posicao" :class="classePodio(eu.posicao)">
                  <span class="rk-eu-num">{{ eu.posicao }}º</span>
                  <span class="rk-eu-de">de {{ eu.total_participantes }}</span>
                </div>
                <div class="rk-eu-resumo">
                  <div class="rk-eu-pontos">
                    <strong>{{ fmtValor(eu.valor) }}</strong> {{ ranking.metricLabel.toLowerCase() }}
                  </div>
                  <div class="rk-eu-selos">
                    <span v-if="eu.no_top && !semPremiacao" class="rk-selo rk-selo-premio">
                      <i class="bx bx-trophy" aria-hidden="true"></i> Zona de premiação
                    </span>
                    <span v-else-if="eu.no_top" class="rk-selo rk-selo-top">TOP {{ dados.top_n }}</span>
                  </div>
                  <p class="rk-eu-meta mb-0">{{ mensagemProgresso }}</p>
                </div>
              </div>
            </template>

            <EmptyState
              v-else-if="eu && eu.participa"
              size="sm"
              icon="bx bx-hourglass"
              title="Sem resultado neste período"
              :text="textoSemResultado"
            />

            <EmptyState
              v-else
              size="sm"
              icon="bx bx-user-x"
              title="Você não participa deste ranking"
              :text="(eu && eu.motivo) || 'Você não aparece entre os participantes deste período.'"
            />

            <template v-if="eu && eu.participa && meusIndicadores.length">
              <h3 class="rk-subsecao-titulo">Seus números no período</h3>
              <dl class="rk-indicadores">
                <div v-for="ind in meusIndicadores" :key="ind.key" class="rk-ind" :class="{ 'is-principal': ind.principal }">
                  <dt :title="ind.label">{{ ind.curto }}</dt>
                  <dd>{{ fmtIndicador(ind.valor, ind.fmt) }}</dd>
                </div>
              </dl>
            </template>
          </div>
        </section>

        <!-- Pódio (DOM na ordem 1-2-3 para leitor de tela; a ordem visual 2-1-3 é só CSS) -->
        <section v-if="podio.length" class="card" aria-labelledby="rkn-podio-titulo">
          <div class="card-body">
            <h2 id="rkn-podio-titulo" class="rk-secao-titulo">Pódio · {{ periodoLabel }}</h2>
            <ol class="rk-podio">
              <li
                v-for="item in podio"
                :key="item.posicao"
                class="rk-podio-item"
                :class="['is-pos-' + item.posicao, { 'is-eu': item.eu }]"
              >
                <span class="sr-only">{{ item.posicao }}º lugar:</span>
                <span class="rk-podio-medalha" aria-hidden="true">{{ MEDALHAS[item.posicao] }}</span>
                <img v-if="item.foto" :src="item.foto" class="rk-avatar rk-avatar-foto" alt="" loading="lazy" />
                <span v-else class="rk-avatar" aria-hidden="true">{{ iniciais(item.nome) }}</span>
                <span class="rk-podio-nome">{{ item.nome }}</span>
                <span v-if="item.eu" class="rk-selo-voce">Você</span>
                <span class="rk-podio-pontos">{{ fmtValor(item.valor) }}</span>
                <span class="rk-podio-base" aria-hidden="true">{{ item.posicao }}º</span>
              </li>
            </ol>
          </div>
        </section>

        <!-- TOP N -->
        <section class="card rk-lista" aria-labelledby="rkn-lista-titulo">
          <div class="card-body pb-2">
            <h2 id="rkn-lista-titulo" class="rk-secao-titulo">TOP {{ dados.top_n }}</h2>
            <p class="rk-secao-sub mb-0">
              <template v-if="!semPremiacao">Todas as {{ dados.top_n }} posições do TOP são premiadas · </template>
              {{ dados.total_participantes }} {{ dados.total_participantes === 1 ? "participante" : "participantes" }} com resultado no período<template v-if="estadual"> na sua diretoria</template>.
              <template v-if="menorVence">Quanto menor a inadimplência, melhor a posição.</template>
              {{ textoDesempate }}
            </p>
          </div>

          <EmptyState
            v-if="!top.length"
            :icon="semPremiacao ? 'bx bx-bar-chart-alt-2' : 'bx bx-trophy'"
            title="Ninguém tem resultado ainda"
            text="Assim que houver resultado no período, o ranking aparece aqui."
          />

          <template v-else>
            <!-- Desktop -->
            <div class="table-responsive d-none d-md-block">
              <table class="table table-hover mb-0 rk-tabela">
                <caption class="sr-only">{{ ranking.titulo }}<template v-if="diretoriaLabel"> — {{ diretoriaLabel }}</template> — {{ periodoLabel }}</caption>
                <thead class="thead-light">
                  <tr>
                    <th scope="col" class="text-center rk-th-pos">#</th>
                    <th scope="col">{{ ranking.entidadeLabel }}</th>
                    <th v-if="mostraRegional" scope="col">Regional</th>
                    <th scope="col" class="text-right">{{ ranking.metricLabel }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in top"
                    :key="item.posicao"
                    :class="{ 'rk-linha-premio': !semPremiacao && item.posicao <= dados.top_n, 'rk-linha-eu': item.eu }"
                  >
                    <td class="text-center rk-td-pos">
                      <span v-if="MEDALHAS[item.posicao]" role="img" :aria-label="item.posicao + 'º lugar'">{{ MEDALHAS[item.posicao] }}</span>
                      <span v-else>{{ item.posicao }}º</span>
                    </td>
                    <td>
                      <div class="rk-pessoa">
                        <img v-if="item.foto" :src="item.foto" class="rk-mini rk-avatar-foto" alt="" loading="lazy" />
                        <span v-else class="rk-mini" aria-hidden="true">{{ iniciais(item.nome) }}</span>
                        <span>
                          <span class="rk-nome">{{ item.nome }}</span>
                          <span v-if="item.eu" class="rk-selo-voce">Você</span>
                        </span>
                      </div>
                    </td>
                    <td v-if="mostraRegional" class="rk-td-regional">{{ item.regional || "—" }}</td>
                    <td class="text-right rk-td-pontos">{{ fmtValor(item.valor) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Celular -->
            <ol class="rk-cards d-md-none">
              <li
                v-for="item in top"
                :key="item.posicao"
                class="rk-card"
                :class="{ 'is-premio': !semPremiacao && item.posicao <= dados.top_n, 'is-eu': item.eu }"
              >
                <div class="rk-card-topo">
                  <span class="rk-card-pos" :class="classePodio(item.posicao)">
                    <span class="sr-only">Posição </span>{{ item.posicao }}º
                  </span>
                  <img v-if="item.foto" :src="item.foto" class="rk-mini rk-avatar-foto" alt="" loading="lazy" />
                  <span v-else class="rk-mini" aria-hidden="true">{{ iniciais(item.nome) }}</span>
                  <div class="rk-card-nome">
                    <span class="rk-nome">{{ item.nome }}</span>
                    <span v-if="item.eu" class="rk-selo-voce">Você</span>
                    <span v-if="mostraRegional && item.regional" class="rk-card-regional">{{ item.regional }}</span>
                  </div>
                  <div class="rk-card-pontos">
                    <strong>{{ fmtValor(item.valor) }}</strong>
                    <span>{{ ranking.metricCurto }}</span>
                  </div>
                </div>
              </li>
            </ol>
          </template>
        </section>
      </template>

      <div v-if="!semDiretoria" class="rk-regulamento">
        <b-button variant="default" :to="regulamento" class="rk-regulamento-btn">
          <i class="bx bx-book-open" aria-hidden="true"></i>
          Ver regulamento e como conferir
        </b-button>
      </div>
    </div>
  </Layout>
</template>

<style scoped src="./ranking.css"></style>

<style scoped>
/* Só o que é específico do Ranking Nacional/Estadual; o resto vem de ranking.css. */

/* Aviso de período anterior a 09/2026: precisa chamar atenção mesmo no celular. */
.rk-aviso-periodo {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border-left: 5px solid #f1b44c;
  font-size: 0.95rem;
  line-height: 1.45;
}
.rk-aviso-periodo-icone {
  font-size: 1.75rem;
  flex-shrink: 0;
  line-height: 1;
}
.rk-diretoria {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 13px; font-weight: 700; letter-spacing: 0.02em; color: var(--st-brand);
}
.rk-diretoria .bx { font-size: 16px; }
@media (min-width: 768px) {
  .rk-filtros-grid-nacional { grid-template-columns: 180px 120px; }
}
.rk-subsecao-titulo {
  font-size: 12px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  color: var(--st-text-muted); margin: 16px 0 0;
}
.rk-ind.is-principal { box-shadow: inset 0 0 0 2px var(--st-brand); }
.rk-ind.is-principal dd { color: var(--st-brand); }
/* Valores em R$ são mais largos que os inteiros do menu "Rankings". */
.rk-card-pontos strong { font-size: 16px; white-space: nowrap; }
.rk-card-pontos span { display: block; font-size: 11px; color: var(--st-text-muted); }
.rk-regulamento { display: flex; justify-content: center; margin: 4px 0 24px; }
.rk-regulamento-btn { display: inline-flex; align-items: center; gap: 6px; min-height: 44px; font-weight: 600; }
.rk-regulamento-btn .bx { font-size: 18px; }
@media (max-width: 575.98px) {
  .rk-regulamento-btn { width: 100%; justify-content: center; }
}
</style>
